// 会員情報機能で共通するユーザー(保護者)に関する表示部分を定義
// 情報照会、変更内容確認で共有
//

import { Stack, styled } from '@mui/material'
import React, { Fragment, ReactNode } from 'react'
import { translate } from '../../../../i18n'
import { GContainer, LabelGItemBase, ValueGItemBase } from '../grids'
import { SubTitle } from '../subTitle'
import { GetCategoryMstDto } from '../../../../dataAccess/webApi/dto/categoryMstDto'
import { Flag } from '../../../../containers/common/constant/classification'

interface User {
  // 基本
  name: string
  kana: string
  postalCode: string
  address1: string
  address2: string
  buildingNameRoomNumber?: string
  residenceCategory: string
  residenceCategoryName: string
  relationship?: string
  timeRequired?: string
  
  // 配偶者情報
  spouseName?: string
  spouseKana?: string
  spouseTel?: string
  spouseEmail?: string
  spouseRelationship?: string
  spouseCompanyName?: string
  spouseCompanyAddress?: string
  spouseCompanyTel?: string
  spouseTimeRequired?: string

  /** 緊急連絡先１～３ */
  emergencyContacts: {
    name?: string
    kana?: string
    relationship?: string
    company?: string
    address?: string
    tel?: string
    email?: string
    timeRequired?: string
    other?: string
  }[]
}

interface UserReference extends User {
  // 減免
  reductionInfos: {
    reductionId: string
    coveredFlag: string
    reductionHistoryFlag: string
    reductionName: string
    reductionStartDate?: Date
    reductionEndDate?: Date
  }[]
}

/** 情報照会時以外で必要なプロパティ */
interface Confirmation {
  user: User
  categoryMap?: {[key:string]: GetCategoryMstDto}
  displayType?: never
}
/** 情報照会時に必要なプロパティ */
interface Reference {
  user: UserReference
  categoryMap?: {[key:string]: GetCategoryMstDto}
  displayType: 'reference'
  onClickReductionHistories: (id: string) => void
}

type UserProps = Confirmation | Reference

const LabelGItem = (props: { children?: ReactNode }) => <LabelGItemBase xs={4}>{props.children}</LabelGItemBase>
const ValueGItem = (props: { children: ReactNode }) => <ValueGItemBase xs={8}>{props.children}</ValueGItemBase>
const AttributeGContainer = styled(GContainer)({
  padding: '0 0 0 3rem',
})

export const USER_PARENT_CATEGORY_KEY = ['timeRequired']
export const USER_SPOUSE_CATEGORY_KEY = [
  'spouseName',
  'spouseKana',
  'spouseTel',
  'spouseEmail',
  'spouseRelationship',
  'spouseCompanyName',
  'spouseCompanyAddress',
  'spouseCompanyTel',
  'spouseTimeRequired'
]
export const USER_EMERGENCY_CONTACT1_CATEGORY_KEY = [
  'emergencyContactName1',
  'emergencyContactKana1',
  'emergencyContactRelationship1',
  'emergencyContactCompany1',
  'emergencyContactAddress1',
  'emergencyContactTel1',
  'emergencyContactEmail1',
  'emergencyContactTimeRequired1',
  'emergencyContactOther1'
]
export const USER_EMERGENCY_CONTACT2_CATEGORY_KEY = [
  'emergencyContactName2',
  'emergencyContactKana2',
  'emergencyContactRelationship2',
  'emergencyContactCompany2',
  'emergencyContactAddress2',
  'emergencyContactTel2',
  'emergencyContactEmail2',
  'emergencyContactTimeRequired2',
  'emergencyContactOther2'
]
export const USER_EMERGENCY_CONTACT3_CATEGORY_KEY = [
  'emergencyContactName3',
  'emergencyContactKana3',
  'emergencyContactRelationship3',
  'emergencyContactCompany3',
  'emergencyContactAddress3',
  'emergencyContactTel3',
  'emergencyContactEmail3',
  'emergencyContactTimeRequired3',
  'emergencyContactOther3'
]

/** 会員情報機能のユーザー(保護者)情報コンテンツ部分 */
export const UserContent = (props: UserProps) => {
  const { categoryMap } = props
  const unSetLabel = translate('system.userContent.label.notSet')

  const isNoData = (keys: string[], categoryMap?: {[key:string]: GetCategoryMstDto}) => {
    if (!categoryMap) return true
    const targetCategories = Object.values(categoryMap).filter((value) => keys.includes(value.category) && value.displayCitizen === Flag.ON)
    if (targetCategories == null || targetCategories.length === 0) {
      return true
    } else {
      return false
    }
  }

  return (
    <Stack spacing={2}>
      {/* 基本 */}
      <Stack spacing={1}>
        <SubTitle title={translate('system.userContent.title.basic')} />
        <div>
          <AttributeGContainer>
            <LabelGItem>{translate('system.userContent.label.userName')}</LabelGItem>
            <ValueGItem>{props.user.name}</ValueGItem>
            <LabelGItem>{translate('system.userContent.label.userNameKana')}</LabelGItem>
            <ValueGItem>{props.user.kana}</ValueGItem>
            <LabelGItem>{translate('system.userContent.label.postalCode')}</LabelGItem>
            <ValueGItem>{props.user.postalCode}</ValueGItem>
            <LabelGItem>{translate('system.userContent.label.prefecturesAndMunicipalities')}</LabelGItem>
            <ValueGItem>{props.user.address1}</ValueGItem>
            <LabelGItem>{translate('system.userContent.label.address')}</LabelGItem>
            <ValueGItem>{props.user.address2}</ValueGItem>
            <LabelGItem>{translate('system.userContent.label.buildingNameRoomNumber')}</LabelGItem>
            <ValueGItem>{props.user.buildingNameRoomNumber}</ValueGItem>
            <LabelGItem>{translate('system.userContent.label.residenceCategory')}</LabelGItem>
            <ValueGItem>{props.user.residenceCategoryName}</ValueGItem>
            <LabelGItem>{translate('system.userContent.label.relationship')}</LabelGItem>
            <ValueGItem>{props.user.relationship}</ValueGItem>
            {categoryMap?.timeRequired && categoryMap?.timeRequired.displayCitizen === Flag.ON &&
              <>
                <LabelGItem>{categoryMap.timeRequired.name}</LabelGItem>
                <ValueGItem>{props.user.timeRequired ? props.user.timeRequired.slice(0, 5) : unSetLabel}</ValueGItem>
              </>
            }
          </AttributeGContainer>
        </div>
      </Stack>
      {/* 配偶者情報 */}
      {categoryMap?.spouse && categoryMap?.spouse.displayCitizen === Flag.ON && !isNoData(USER_SPOUSE_CATEGORY_KEY, categoryMap) &&
        <>
          <Stack spacing={1}>
            <SubTitle title={categoryMap.spouse.name} />
            <div>
              <AttributeGContainer>
              {categoryMap?.spouseName && categoryMap?.spouseName.displayCitizen === Flag.ON &&
                <>
                  <LabelGItem>{categoryMap.spouseName.name}</LabelGItem>
                  <ValueGItem>{props.user.spouseName || unSetLabel}</ValueGItem>
                </>
              }
              {categoryMap?.spouseKana && categoryMap?.spouseKana.displayCitizen === Flag.ON &&
                <>
                  <LabelGItem>{categoryMap.spouseKana.name}</LabelGItem>
                  <ValueGItem>{props.user.spouseKana || unSetLabel}</ValueGItem>
                </>
              }
              {categoryMap?.spouseTel && categoryMap?.spouseTel.displayCitizen === Flag.ON &&
                <>
                  <LabelGItem>{categoryMap.spouseTel.name}</LabelGItem>
                  <ValueGItem>{props.user.spouseTel || unSetLabel}</ValueGItem>
                </>
              }
              {categoryMap?.spouseEmail && categoryMap?.spouseEmail.displayCitizen === Flag.ON &&
                <>
                  <LabelGItem>{categoryMap.spouseEmail.name}</LabelGItem>
                  <ValueGItem>{props.user.spouseEmail || unSetLabel}</ValueGItem>
                </>
              }
              {categoryMap?.spouseRelationship && categoryMap?.spouseRelationship.displayCitizen === Flag.ON &&
                <>
                  <LabelGItem>{categoryMap.spouseRelationship.name}</LabelGItem>
                  <ValueGItem>{props.user.spouseRelationship || unSetLabel}</ValueGItem>
                </>
              }
              {categoryMap?.spouseCompanyName && categoryMap?.spouseCompanyName.displayCitizen === Flag.ON &&
                <>
                  <LabelGItem>{categoryMap.spouseCompanyName.name}</LabelGItem>
                  <ValueGItem>{props.user.spouseCompanyName || unSetLabel}</ValueGItem>
                </>
              }
              {categoryMap?.spouseCompanyAddress && categoryMap?.spouseCompanyAddress.displayCitizen === Flag.ON &&
                <>
                  <LabelGItem>{categoryMap.spouseCompanyAddress.name}</LabelGItem>
                  <ValueGItem>{props.user.spouseCompanyAddress || unSetLabel}</ValueGItem>
                </>
              }
              {categoryMap?.spouseCompanyTel && categoryMap?.spouseCompanyTel.displayCitizen === Flag.ON &&
                <>
                  <LabelGItem>{categoryMap.spouseCompanyTel.name}</LabelGItem>
                  <ValueGItem>{props.user.spouseCompanyTel || unSetLabel}</ValueGItem>
                </>
              }
              {categoryMap?.spouseTimeRequired && categoryMap?.spouseTimeRequired.displayCitizen === Flag.ON &&
                <>
                  <LabelGItem>{categoryMap.spouseTimeRequired.name}</LabelGItem>
                  <ValueGItem>{props.user.spouseTimeRequired ? props.user.spouseTimeRequired.slice(0, 5) : unSetLabel}</ValueGItem>
                </>
              }
              </AttributeGContainer>
            </div>
          </Stack>
        </>
      }
      {/* 緊急連絡先１～３ */}
      {['emergencyContact1', 'emergencyContact2', 'emergencyContact3'].map((title, index) => {
        const emergencyContact = index + 1
        let targetKeys: string[] = []
        if (emergencyContact === 1) {
          targetKeys = USER_EMERGENCY_CONTACT1_CATEGORY_KEY
        }
        if (emergencyContact === 2) {
          targetKeys = USER_EMERGENCY_CONTACT2_CATEGORY_KEY
        }
        if (emergencyContact === 3) {
          targetKeys = USER_EMERGENCY_CONTACT3_CATEGORY_KEY          
        }
        if (isNoData(targetKeys, categoryMap)) {
          return null
        } else {
          const contact = props.user.emergencyContacts[index]
          return (
            <Stack key={title} spacing={1}>
              <SubTitle title={translate(`system.userContent.title.${title}`)} />
              <div>
                <AttributeGContainer>
                {categoryMap && categoryMap[`emergencyContactName${index+1}`] && categoryMap[`emergencyContactName${index+1}`]?.displayCitizen === Flag.ON &&
                  <>
                    <LabelGItem>{categoryMap[`emergencyContactName${index+1}`].name}</LabelGItem>
                    <ValueGItem>{contact.name || unSetLabel}</ValueGItem>
                  </>
                }
                {categoryMap && categoryMap[`emergencyContactKana${index+1}`] && categoryMap[`emergencyContactKana${index+1}`]?.displayCitizen === Flag.ON &&
                  <>
                    <LabelGItem>{categoryMap[`emergencyContactKana${index+1}`].name}</LabelGItem>
                    <ValueGItem>{contact.kana || unSetLabel}</ValueGItem>
                  </>
                }
                {categoryMap && categoryMap[`emergencyContactRelationship${index+1}`] && categoryMap[`emergencyContactRelationship${index+1}`]?.displayCitizen === Flag.ON &&
                  <>
                    <LabelGItem>{categoryMap[`emergencyContactRelationship${index+1}`].name}</LabelGItem>
                    <ValueGItem>{contact.relationship || unSetLabel}</ValueGItem>
                  </>
                }
                {categoryMap && categoryMap[`emergencyContactCompany${index+1}`] && categoryMap[`emergencyContactCompany${index+1}`]?.displayCitizen === Flag.ON &&
                  <>
                    <LabelGItem>{categoryMap[`emergencyContactCompany${index+1}`].name}</LabelGItem>
                    <ValueGItem>{contact.company || unSetLabel}</ValueGItem>
                  </>
                }
                {categoryMap && categoryMap[`emergencyContactAddress${index+1}`] && categoryMap[`emergencyContactAddress${index+1}`]?.displayCitizen === Flag.ON &&
                  <>
                    <LabelGItem>{categoryMap[`emergencyContactAddress${index+1}`].name}</LabelGItem>
                    <ValueGItem>{contact.address || unSetLabel}</ValueGItem>
                  </>
                }
                {categoryMap && categoryMap[`emergencyContactTel${index+1}`] && categoryMap[`emergencyContactTel${index+1}`]?.displayCitizen === Flag.ON &&
                  <>
                    <LabelGItem>{categoryMap[`emergencyContactTel${index+1}`].name}</LabelGItem>
                    <ValueGItem>{contact.tel || unSetLabel}</ValueGItem>
                  </>
                }
                {categoryMap && categoryMap[`emergencyContactEmail${index+1}`] && categoryMap[`emergencyContactEmail${index+1}`]?.displayCitizen === Flag.ON &&
                  <>
                    <LabelGItem>{categoryMap[`emergencyContactEmail${index+1}`].name}</LabelGItem>
                    <ValueGItem>{contact.email || unSetLabel}</ValueGItem>
                  </>
                }
                {categoryMap && categoryMap[`emergencyContactTimeRequired${index+1}`] && categoryMap[`emergencyContactTimeRequired${index+1}`]?.displayCitizen === Flag.ON &&
                  <>
                    <LabelGItem>{categoryMap[`emergencyContactTimeRequired${index+1}`].name}</LabelGItem>
                    <ValueGItem>{contact.timeRequired ? contact.timeRequired.slice(0, 5) : unSetLabel}</ValueGItem>
                  </>
                }
                {categoryMap && categoryMap[`emergencyContactOther${index+1}`] && categoryMap[`emergencyContactOther${index+1}`]?.displayCitizen === Flag.ON &&
                  <>
                    <LabelGItem>{categoryMap[`emergencyContactOther${index+1}`].name}</LabelGItem>
                    <ValueGItem>{contact.other || unSetLabel}</ValueGItem>
                  </>
                }
                </AttributeGContainer>
              </div>
            </Stack>
          )  
        }
      })}

      {/* 減免 */}
      {/* 減免情報の表示が不要となったので一旦コメントアウト
      <Stack spacing={1}>
        <SubTitle title={translate('system.userContent.title.exempt')} />
        {props.displayType === 'reference' ? (
          <div>
            <AttributeGContainer>
              {props.user.reductionInfos.map((v, index) => {
                return (
                  <Fragment key={index}>
                    <LabelGItem>{v.reductionName}</LabelGItem>
                    <ValueGItem>
                      {getCoveredLabel(v.coveredFlag)}
                      <div>
                        {v.coveredFlag === yesNo.yes && (v.reductionStartDate || v.reductionEndDate) && (
                          <>
                            ({v.reductionStartDate && formatYmd(v.reductionStartDate)}～
                            {v.reductionEndDate && formatYmd(v.reductionEndDate)})
                          </>
                        )}
                      </div>
                    </ValueGItem>
                    {v.reductionHistoryFlag === yesNo.yes && (
                      <RightGItem xs={12}>
                        <Link onClick={() => props.onClickReductionHistories(v.reductionId)}>{`${translate(
                          'system.button.detail'
                        )}>`}</Link>
                      </RightGItem>
                    )}
                  </Fragment>
                )
              })}
            </AttributeGContainer>
          </div>
        ) : (
          <AttributeGContainer>
            <GItem xs={12}>{translate('system.userContent.text.exempt')}</GItem>
          </AttributeGContainer>
        )}
      </Stack>
      */}
    </Stack>
  )
}
